/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { ReactNode } from 'react';

// eslint-disable-next-line react-refresh/only-export-components
const DescriptionFR = () => (
  <div className="w-full w-25rem text-sm line-height-3">
    <p>Score Carbone du Traitement de Données</p>
    <p>
      Ce score évalue l’empreinte carbone générée par un processus de traitement de données. Il est établi à partir de
      plusieurs critères clés :
    </p>
    <ol>
      <li>
        <strong>Optimisation des Données</strong> (qualité de la compression, organisation du stockage, retraitement
        pour réduire le volume des données)
      </li>
      <li>
        <strong>Durée d’Exécution</strong> (temps nécessaire au traitement, qui influe sur la consommation énergétique)
      </li>
      <li>
        <strong>Compensation ou Réduction</strong> (mécanismes d’offset carbone, usage d’énergies renouvelables)
      </li>
      <li>
        <strong>Utilisation du GPU</strong> (accélération matérielle plus ou moins gourmande en énergie selon le modèle,
        la charge de travail et la configuration matérielle)
      </li>
    </ol>

    <p>
      <strong>A</strong> &rarr; Faible impact carbone
      <br />
      <strong>E</strong> &rarr; Impact carbone très élevé
      <br />
      <strong>?</strong> &rarr; Score non disponible ou indéfini
    </p>
  </div>
);

// eslint-disable-next-line react-refresh/only-export-components
const DescriptionEN = () => (
  <div className="w-full w-25rem text-sm line-height-3">
    <p>Data Processing Carbon Score</p>
    <p>
      This score evaluates the carbon footprint produced by a data processing workflow. It is determined based on
      several key criteria:
    </p>
    <ol>
      <li>
        <strong>Data Optimization</strong> (the quality of compression, storage organization, and reprocessing to reduce
        data volume)
      </li>
      <li>
        <strong>Execution Time</strong> (how long processing takes, which impacts energy consumption)
      </li>
      <li>
        <strong>Carbon Offsetting or Reduction</strong> (offset mechanisms, reliance on renewable energy sources)
      </li>
      <li>
        <strong>GPU Utilization</strong> (hardware acceleration that may be more or less energy-intensive depending on
        model, workload, and configuration)
      </li>
    </ol>

    <p>
      <strong>A</strong> &rarr; Low carbon impact
      <br />
      <strong>E</strong> &rarr; Very high carbon impact
      <br />
      <strong>?</strong> &rarr; Score unavailable or undefined
    </p>
  </div>
);

export const carbonDescription: { [key: string]: ReactNode } = {
  fr: DescriptionFR(),
  en: DescriptionEN()
};
